import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { DxScrollViewModule, DxValidatorModule } from 'devextreme-angular';
import { DxSortableComponent } from 'devextreme-angular/ui/sortable';
import { DxValidatorComponent } from 'devextreme-angular/ui/validator';
import { DxiValidationRuleComponent } from 'devextreme-angular/ui/nested';
import { GpdButtonComponent } from '../../../../../../../components/gpd-button/gpd-button.component';
import { SetupPhaseNodeComponent } from '../../node-types/setup-phase-node/setup-phase-node.component';
import { MultiStepFormComponent } from '../../../../../../../components/multi-step-form/multi-step-form.component';
import { Phase } from '../../../../../../../interfaces/entities/project-configuration';

@Component({
    selector: 'app-setup-phases-step',
    imports: [
        CommonModule,
        SetupPhaseNodeComponent,
        DxSortableComponent,
        DxScrollViewModule,
        DxValidatorComponent,
        DxiValidationRuleComponent,
        DxValidatorModule,
        GpdButtonComponent,
    ],
    templateUrl: './setup-phases-step.component.html',
    styleUrl: './setup-phases-step.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SetupPhasesStepComponent {
  @Input() phases: Phase[] = [];
  @Input() validationGroup: any;
  @Input() multiStepForm?: MultiStepFormComponent;

  @Output() phasesChange = new EventEmitter<Phase[]>();
  @Output() validationUpdate = new EventEmitter<void>();

  // Callbacks
  phaseValidationCallbacks: Array<() => void> = [];

  constructor(private cdr: ChangeDetectorRef) {}

  phaseAdapterConfig = {
    getValue: () => {
      return this.phaseValidationCallback();
    },
    validationRequestsCallbacks: this.phaseValidationCallbacks,
  };

  phaseValidationCallback = () => {
    // Check if we have at least one phase
    if (this.phases.length === 0) {
      return false;
    }

    // Check each phase for validity
    for (const phase of this.phases) {
      // Check if phase has a name
      if (!phase.title || phase.title.trim() === '') {
        return false;
      }

      // Check for duplicate names (case-insensitive)
      const duplicateExists = this.phases.some((otherPhase, index) => {
        return (
          otherPhase !== phase &&
          otherPhase.title &&
          otherPhase.title.trim().toLowerCase() === phase.title.trim().toLowerCase()
        );
      });

      if (duplicateExists) {
        return false;
      }
    }

    return true;
  };

  addNewPhase = () => {
    // Generate temporary negative ID for new phases
    const tempId = Math.min(...this.phases.map(p => p.id || 0), 0) - 1;

    const newPhase: Phase = {
      id: tempId,
      title: '',
      showInGantt: true,
      milestones: [],
    };
    this.phases.push(newPhase);
    this.phasesChange.emit(this.phases);
    this.cdr.detectChanges();

    // Trigger validation update after DOM updates to register new validators
    this.multiStepForm?.resetValidation();
    // Trigger step-level validation
    this.phaseValidationCallbacks.forEach(callback => callback());
  };

  onPhaseDeleted = (index: number) => {
    this.phases.splice(index, 1);
    this.phasesChange.emit(this.phases);
    this.cdr.detectChanges();

    // Trigger validation update after DOM updates
    this.multiStepForm?.triggerValidationUpdate();
    // Trigger step-level validation
    this.phaseValidationCallbacks.forEach(callback => callback());
  };

  onPhaseReorder = (event: any) => {
    const { fromIndex, toIndex, fromData, toData } = event;

    if (fromIndex !== toIndex) {
      var data = fromData[fromIndex];
      fromData.splice(fromIndex, 1);
      toData.splice(toIndex, 0, data);

      this.phasesChange.emit([...this.phases]);
      this.cdr.detectChanges();

      // Trigger validation update after reordering
      this.multiStepForm?.triggerValidationUpdate();
      // Trigger step-level validation
      this.phaseValidationCallbacks.forEach(callback => callback());
    }
  };

  onPhaseValidationChange = () => {
    // Trigger step-level validation when any phase validation changes
    this.phaseValidationCallbacks.forEach(callback => callback());
  };
}
